/**
 * Dates for display, on BOTH sides of the wire: the one date formatter (MAR-948).
 *
 * Framework-free on purpose, so the server formats with it too. The client reaches it through
 * `useDateHelper()`, which binds the user's i18n locale. The server has no user locale to bind, so
 * it passes one explicitly: a generated Spanish document passes `'es'`. What it must never do is
 * call `toLocaleDateString()`, whose output depends on the RUNTIME's locale and zone (and on SSR
 * pages, differs between server and browser: a hydration mismatch).
 * `tests/unit/formatHelpersSweep.test.ts` fails on a hand-formatted date anywhere in
 * app/, shared/ or server/. Relative time ("hace 3 h") lives in `app/utils/relativeTime.ts`.
 */
export type DateInput = string | number | Date | null | undefined

type DateStyle = 'short' | 'medium' | 'long' | 'full'
type TimeStyle = 'short' | 'medium' | 'long' | 'full'

const DEFAULT_LOCALE = 'es-SV'
const LOCALE_BY_CODE: Record<string, string> = {
  es: 'es-SV',
  en: 'en-US',
}

export class DateHelper {
  static resolveLocale(locale?: string | null): string {
    if (!locale) return DEFAULT_LOCALE
    return LOCALE_BY_CODE[locale] ?? locale
  }

  static formatDateForDisplay(
    date: DateInput,
    locale?: string | null,
    dateStyle: DateStyle = 'medium',
  ): string {
    const parsed = DateHelper.parse(date)
    if (!parsed) return ''

    return new Intl.DateTimeFormat(DateHelper.resolveLocale(locale), {
      dateStyle,
    }).format(parsed)
  }

  static formatDateTimeForDisplay(
    date: DateInput,
    locale?: string | null,
    dateStyle: DateStyle = 'medium',
    timeStyle: TimeStyle = 'short',
  ): string {
    const parsed = DateHelper.parse(date)
    if (!parsed) return ''

    return new Intl.DateTimeFormat(DateHelper.resolveLocale(locale), {
      dateStyle,
      timeStyle,
    }).format(parsed)
  }

  static formatTimeForDisplay(
    date: DateInput,
    locale?: string | null,
    timeStyle: TimeStyle = 'short',
  ): string {
    const parsed = DateHelper.parse(date)
    if (!parsed) return ''

    return new Intl.DateTimeFormat(DateHelper.resolveLocale(locale), {
      timeStyle,
    }).format(parsed)
  }

  /**
   * For a layout the presets above do not cover (a bare month name, `dd/mm/yyyy`, a short date with
   * a time). Same locale resolution and same empty-string-for-invalid rule as the presets.
   */
  static formatWithOptions(
    date: DateInput,
    locale: string | null | undefined,
    options: Intl.DateTimeFormatOptions,
  ): string {
    const parsed = DateHelper.parse(date)
    if (!parsed) return ''

    return new Intl.DateTimeFormat(DateHelper.resolveLocale(locale), options).format(parsed)
  }

  static parse(date: DateInput): Date | null {
    if (date === null || date === undefined || date === '') return null
    if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date

    if (typeof date === 'string') {
      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch
        return new Date(Number(year), Number(month) - 1, Number(day))
      }
    }

    const parsed = new Date(date)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
}

/**
 * All-digit day/month/year (`24/9/2026` in Spanish), what `toLocaleDateString('es-SV')` printed.
 * For documents that carry a date in a fixed locale, such as the memo in `generate-memo.post.ts`.
 */
export const NUMERIC_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric', year: 'numeric' }
